import React from 'react'
import { Product } from '../../models/Product';
import './ProductList.css';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { updateOrder } from '../../store/slices/orderSlice';
import { useTranslation } from 'react-i18next';
import { CarouselStep } from '../../utils/CarouselSteps';
import { isReportRunnerProduct } from '../../utils/reportRunner';
import ProductListSkeleton from './ProductListSkeleton';

interface Props {
    productList: Product[]
    isLoading: boolean
}

const ProductList: React.FunctionComponent<Props> = ({ productList, isLoading }: Props) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const order = useAppSelector((rootState) => rootState.order);
    const mainProducts = productList.filter((product) => !isReportRunnerProduct(product));
    const reportRunnerProduct = productList.find((product) => isReportRunnerProduct(product));

    if (isLoading) return <ProductListSkeleton />;

    const onProductSelection = (product: Product | null | undefined) => {
        if (!product) return;
        let orderState = { ...order };
        orderState.product = product;
        if (isReportRunnerProduct(product)) {
            orderState.professionalId = 0;
            orderState.carouselIndex = CarouselStep.OrderUser;
        } else if (product.id === 2) {
            orderState.carouselIndex = CarouselStep.ProfessionalList;
        } else {
            orderState.carouselIndex = CarouselStep.ReferenceYear;
        }
        dispatch(updateOrder(orderState));
    }

    const getProductDescription = (descriptionStr: string) => {
        if (!descriptionStr) return null;
        const splitted = descriptionStr.split("|").map((item) => item.trim()).filter(Boolean);
        return splitted.map((item, index) => {
            return <li key={index}><span dangerouslySetInnerHTML={{ __html: item }}></span></li>;
        });
    }

    const getProductTitle = (product: Product) => {
        const productTitleKey = product.pricingTableSubtitleLanguageKey?.replace(
            'order.productlist.productSubtitle.',
            'order.productlist.productTitle.'
        );

        return productTitleKey ? t(productTitleKey, { defaultValue: product.name }) : product.name;
    }

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <h1 dangerouslySetInnerHTML={{ __html: t('order.productlist.chooseProduct') }}></h1>
                    <h5 dangerouslySetInnerHTML={{ __html: t('order.productlist.pricingInfo1') }}></h5>
                </div>
            </div>
            <div className="row">
                <div className="col-12">
                    <div className="pricingTableList">
                        {mainProducts.map((product, index) => {
                            return (
                                <div
                                    key={product.id}
                                    className={`pricingTable${index === 1 ? ' pricingTableFeatured' : ''}`}
                                >
                                    <div className="pricingTableHeading">
                                        <h2 className="pricingTableTitle">{getProductTitle(product)}</h2>
                                        <span className="pricingTableBestValue" dangerouslySetInnerHTML={{ __html: t(product.pricingTableSubtitleLanguageKey) }}></span>
                                    </div>
                                    <div className="pricingTableContentTop">
                                        <span className="pricingTablePrice">
                                            <span className="pricingTableDollarSign">CHF </span>
                                            <span className="pricingTableSum">{product.basePrice}</span>
                                            <span className="pricingTableFrequency">
                                                <span className="pricingTableFrequencySlash">/</span>
                                                {t('order.productlist.perYear')}
                                            </span>
                                        </span>
                                    </div>
                                    <div className="pricingTableContent">
                                        <ul className="pricingList">
                                            {getProductDescription(t(product.pricingTableDescriptionLanguageKey))}
                                        </ul>
                                    </div>
                                    <div className="pricingTableActionWrapper">
                                        <button
                                            type="button"
                                            className="productListIconAction pricingTableAction"
                                            onClick={() => onProductSelection(product)}
                                        >
                                            {t('order.productlist.orderButton')}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            {reportRunnerProduct ? (
                <div className="row reportRunnerSection">
                    <div className="col-12">
                        <p
                            className="reportRunnerInfo"
                            dangerouslySetInnerHTML={{ __html: t("order.productlist.reportRunnerInfo") }}
                        ></p>
                        <div className="reportRunnerActionWrapper">
                            <button
                                type="button"
                                className="productListIconAction marketingAction reportRunnerAction"
                                onClick={() => onProductSelection(reportRunnerProduct)}
                            >
                                {t('order.productlist.reportRunnerButton')}
                            </button>
                        </div>
                        <p className="reportRunnerDisclaimer" dangerouslySetInnerHTML={{ __html: t("order.productlist.reportRunnerDisclaimer") }}></p>
                    </div>
                </div>
            ) : null}
            <div className='row'>
                <div className='col-12'>
                    <p dangerouslySetInnerHTML={{ __html: t('order.productlist.pricingInfo2') }}></p>
                </div>
            </div>
        </>
    )
}

export default ProductList
